import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CloseIcon } from "./Icons.tsx";

/**
 * The bottom sheet every owner action happens in.
 *
 * A sheet rather than a page, because every one of these actions is a decision about a row the
 * owner is already looking at, and losing the list behind it costs the context that makes the
 * decision safe. It rises from the bottom for the same reason the tab bar lives there: on a 390px
 * phone the bottom third is the only part a thumb reaches without a regrip.
 *
 * The mobile app uses `Alert.alert` for confirmations. The web has no equivalent that is not a
 * `window.confirm` box in the browser's own typeface, so a confirm is a second mode inside this
 * same sheet: same silhouette, same buttons, one step deeper.
 *
 * Built on `<dialog>` so the browser supplies the focus trap, the Escape key, the backdrop and the
 * inert background, rather than six hand-rolled effects that each get it slightly wrong.
 *
 * Rendered into `document.body`, so it always takes the page's own tokens. A sheet opened from the
 * dark Control Center sidebar (sign out) must not inherit that sidebar's `data-tone="dark"`.
 */
export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  actions,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Pinned to the bottom of the sheet, above the home indicator, where the thumb already is. */
  actions?: ReactNode;
  children?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      // happy-dom ships the element without the modal machinery, so the tests fall back to the
      // plain `open` attribute. The markup under test is identical either way.
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }

    /*
     * Backdrop dismissal, on a native listener rather than a React `onClick`.
     *
     * The dialog element fills the viewport and its ::backdrop is not a node, so a click that lands
     * on the dialog itself rather than on the panel inside it is a click on the backdrop. Attaching
     * it here rather than in JSX keeps the markup free of a click handler on a non-interactive
     * element, which is the shape the accessibility rules are written to catch.
     */
    const onBackdropClick = (event: MouseEvent) => {
      if (event.target === dialog) onClose();
    };
    dialog.addEventListener("click", onBackdropClick);
    return () => dialog.removeEventListener("click", onBackdropClick);
  }, [onClose]);

  if (!open) return null;

  return createPortal(
    <dialog
      ref={ref}
      aria-label={title}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      className={[
        "fixed inset-0 z-50 m-0 flex h-full max-h-none w-full max-w-none items-end justify-center",
        "bg-ink/35 p-0 text-ink backdrop:bg-ink/35 backdrop:backdrop-blur-[2px]",
      ].join(" ")}
    >
      {/*
        The panel takes focus, not the first control in it.
        `showModal` otherwise focuses the close button, which puts a focus ring on the one control
        that is not the point of the sheet. Focusing the panel keeps the trap and the Escape key,
        and leaves the ring for whatever the owner actually tabs to.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        tabIndex={-1}
        className="pb-safe w-full max-w-app rounded-t-[22px] border-t border-line bg-paper shadow-[0_-8px_40px_-16px_rgba(16,25,23,0.45)] outline-none motion-safe:animate-sheet"
      >
        <div className="flex items-start gap-3 px-5 pt-3 pb-3">
          <div className="min-w-0 flex-1">
            <span
              className="mx-auto mb-3 block h-1 w-9 rounded-full bg-line-strong"
              aria-hidden="true"
            />
            <h2 className="text-[21px]">{title}</h2>
            {subtitle ? <p className="mt-1 text-[13px] text-ink-2">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            // Named after the panel rather than a bare "Close", because a sheet very often has a
            // Close button of its own among the actions and two identical labels help nobody.
            aria-label={`Close ${title}`}
            className="mt-4 flex size-9 shrink-0 items-center justify-center rounded-full border border-line text-ink-2 transition-colors duration-150 active:bg-sunken"
          >
            <CloseIcon size={17} />
          </button>
        </div>

        <div className="max-h-[56vh] overflow-y-auto overscroll-contain px-5 pb-1">{children}</div>

        {actions ? (
          <div className="flex flex-col gap-2 border-t border-line px-5 pt-4 pb-5">{actions}</div>
        ) : (
          <div className="h-5" />
        )}
      </div>
    </dialog>,
    document.body,
  );
}
